import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ChevronLeft, ChevronRight, Clock, Loader2, Send } from "lucide-react";
import api from "@/utils/api";

const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

const Option = ({ opt, selected, multi, onClick, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className={`w-full text-left flex items-start gap-4 rounded-2xl border px-5 py-4 transition-colors duration-200 ${selected ? "border-[#4ECDC4] bg-[#4ECDC4]/10" : "border-white/10 bg-white/[0.02] hover:border-white/25"}`}>
    <span className={`mt-0.5 w-6 h-6 shrink-0 flex items-center justify-center border ${multi ? "rounded-md" : "rounded-full"} ${selected ? "bg-[#4ECDC4] border-[#4ECDC4] text-[#0A1029]" : "border-white/30 text-white/50"}`}>
      {selected ? <Check className="w-3.5 h-3.5" /> : <span className="text-[11px] font-semibold">{opt.key}</span>}
    </span>
    <span className="text-white/85 text-sm md:text-base leading-relaxed">{opt.text}</span>
  </button>
);

export const QuizRunner = ({ attempt, questions, initialAnswers, serverNow, onSubmitted }) => {
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState(initialAnswers || {});
  const [submitting, setSubmitting] = useState(false);
  const skew = useRef(new Date(serverNow).getTime() - Date.now());
  const deadline = new Date(attempt.expires_at).getTime();
  const [left, setLeft] = useState(Math.max(0, Math.round((deadline - Date.now() - skew.current) / 1000)));
  const answersRef = useRef(answers);
  answersRef.current = answers;

  const submit = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/api/quiz/attempts/${attempt.attempt_id}/submit`, { answers: answersRef.current });
      onSubmitted(data);
    } catch {
      setSubmitting(false);
    }
  }, [attempt.attempt_id, onSubmitted, submitting]);

  useEffect(() => {
    const t = setInterval(() => {
      const s = Math.max(0, Math.round((deadline - Date.now() - skew.current) / 1000));
      setLeft(s);
      if (s === 0) { clearInterval(t); submit(); }
    }, 1000);
    return () => clearInterval(t);
  }, [deadline, submit]);

  useEffect(() => {
    const t = setTimeout(() => {
      api.put(`/api/quiz/attempts/${attempt.attempt_id}/answers`, { answers }).catch(() => {});
    }, 600);
    return () => clearTimeout(t);
  }, [answers, attempt.attempt_id]);

  const q = questions[idx];
  const sel = answers[q.qid] || [];
  const toggle = (key) => setAnswers((a) => {
    const cur = a[q.qid] || [];
    const next = q.multi ? (cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key].sort()) : [key];
    return { ...a, [q.qid]: next };
  });
  const answered = questions.filter((x) => (answers[x.qid] || []).length).length;
  const last = idx === questions.length - 1;

  return (
    <div className="max-w-3xl mx-auto" data-testid="quiz-runner">
      <div className="flex items-center justify-between gap-4 mb-5">
        <span className="text-sm text-white/55" data-testid="quiz-progress">Question {idx + 1} of {questions.length} · {answered} answered</span>
        <span className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full border font-mono text-sm ${left <= 60 ? "border-[#FF6B6B] text-[#FF6B6B] bg-[#FF6B6B]/10" : "border-white/15 text-white"}`} data-testid="quiz-timer">
          <Clock className="w-4 h-4" /> {fmt(left)}
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-white/5 overflow-hidden mb-8">
        <div className="h-full bg-gradient-to-r from-[#4ECDC4] via-[#FFE66D] to-[#FF6B6B] transition-[width] duration-500" style={{ width: `${((idx + 1) / questions.length) * 100}%` }} />
      </div>
      <AnimatePresence mode="wait">
        <motion.div key={q.qid} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.25 }}
          className="glass-card rounded-3xl p-6 md:p-8" data-testid={`quiz-question-${idx}`}>
          <p className="text-xs uppercase tracking-[0.15em] mb-3" style={{ color: q.multi ? "#FFE66D" : "#4ECDC4" }} data-testid="quiz-question-kind">
            {q.multi ? "Choose all that apply" : q.document === 2 ? "About you" : "Choose one"}
          </p>
          <h2 className="font-display text-xl md:text-2xl text-white leading-snug mb-6" data-testid="quiz-question-text">{q.question}</h2>
          <div className="space-y-3">
            {q.options.map((o) => (
              <Option key={o.key} opt={o} multi={q.multi} selected={sel.includes(o.key)} onClick={() => toggle(o.key)} testId={`quiz-option-${o.key}`} />
            ))}
          </div>
        </motion.div>
      </AnimatePresence>
      <div className="flex items-center justify-between mt-6 gap-3">
        <button onClick={() => setIdx(idx - 1)} disabled={idx === 0} data-testid="quiz-prev-btn"
          className="btn-glass px-5 py-2.5 rounded-full text-white inline-flex items-center gap-1.5 disabled:opacity-30"><ChevronLeft className="w-4 h-4" /> Back</button>
        <div className="hidden sm:flex gap-1.5">
          {questions.map((x, i) => (
            <button key={x.qid} onClick={() => setIdx(i)} aria-label={`Go to question ${i + 1}`} data-testid={`quiz-dot-${i}`}
              className={`w-2.5 h-2.5 rounded-full ${i === idx ? "bg-white" : (answers[x.qid] || []).length ? "bg-[#4ECDC4]" : "bg-white/20"}`} />
          ))}
        </div>
        {last ? (
          <button onClick={submit} disabled={submitting} data-testid="quiz-submit-btn"
            className="btn-coral px-6 py-2.5 rounded-full font-semibold inline-flex items-center gap-2 disabled:opacity-60">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Submit test
          </button>
        ) : (
          <button onClick={() => setIdx(idx + 1)} data-testid="quiz-next-btn"
            className="btn-coral px-6 py-2.5 rounded-full font-semibold inline-flex items-center gap-1.5">Next <ChevronRight className="w-4 h-4" /></button>
        )}
      </div>
    </div>
  );
};
